import { UISref } from "@uirouter/react";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { merge, Subject } from "rxjs";
import { debounceTime, filter } from "rxjs/operators";
import styled from "styled-components";
import { ReportIssueRoutes } from "../../pages/report-issue/routes";
import { colours } from "../../styles/colours";
import { Menu, MenuItem, MenuNode } from "../menu";
import { WhiteItem } from "../menu/item/white";
import { animationDelay, SidebarBase, SidebarBasePropTypes } from "./base";
import { useSidebarNodes } from "./hooks";

const ReportIssueMenuItem = styled(WhiteItem)`
  color: ${colours.red};
`;

function useSidebarAnimation() {
  const [immediateCollapsed, setImmediateCollapsed] = useState(false);
  const [delayedCollapsed, setDelayedCollapsed] = useState(false);
  const collapsedSubject = useMemo(() => new Subject<boolean>(), []);

  useEffect(() => {
    // Merging 2 streams: if we close sidebar we hide text immediately
    // otherwise, on open, we wait for animation to finish and then show text
    const collapsed$ = merge(
      collapsedSubject.pipe(
        filter((x) => !x),
        debounceTime(animationDelay)
      ),
      collapsedSubject.pipe(filter((x) => x))
    );

    const sub = collapsed$.subscribe(setDelayedCollapsed);
    return () => sub.unsubscribe();
  }, [collapsedSubject]);

  const toggleCollapsed = () => {
    collapsedSubject.next(!immediateCollapsed);
    setImmediateCollapsed(!immediateCollapsed);
  };

  return [immediateCollapsed, delayedCollapsed, toggleCollapsed] as const;
}

export type DesktopSidebarPropTypes = Omit<
  SidebarBasePropTypes,
  "immediateCollapsed" | "delatyedCollapsed" | "toggleCollapsed"
>;

export const DesktopSidebar: React.FunctionComponent<DesktopSidebarPropTypes> = (
  props
) => {
  const sidebarListWithSelection = useSidebarNodes();
  const { t } = useTranslation("common");

  const [
    immediateCollapsed,
    delayedCollapsed,
    toggleCollapsed,
  ] = useSidebarAnimation();

  return (
    <SidebarBase
      {...props}
      immediateCollapsed={immediateCollapsed}
      delayedCollapsed={delayedCollapsed}
      toggleCollapsed={toggleCollapsed}
    >
      <Menu>
        {sidebarListWithSelection.map((node) => (
          <MenuItem
            node={node}
            key={
              MenuNode.isLeafNode(node)
                ? MenuNode.isLeafTargeted(node)
                  ? t(node.title)
                  : t(node.route.data.title)
                : node.title
            }
            compact={delayedCollapsed}
            routeOnOpen={true}
          />
        ))}
        <a
          href="https://sekuritance.com/about-us/"
          target="_blank"
          rel="noreferrer"
        >
          <WhiteItem icon="aboutUs" variant="leaf" compact={delayedCollapsed}>
            {t("about-us")}
          </WhiteItem>
        </a>
        <UISref to={ReportIssueRoutes.names.index}>
          <a>
            <ReportIssueMenuItem variant="leaf" compact={delayedCollapsed}>
              {t("report-an-issue")}
            </ReportIssueMenuItem>
          </a>
        </UISref>
      </Menu>
    </SidebarBase>
  );
};
